import React, { useState, useEffect, useRef } from 'react';
import haukeApiService from '../services/haukeApi';

const ProducerSelector = ({ 
  selectedProducer, 
  onProducerSelect, 
  currentFarmId,
  className = '',
  resultsInFlow = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProducers, setAllProducers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const lastRequestIdRef = useRef(0);
  const searchQueryRef = useRef('');

  // Initial: leichte Vorbefüllung (erste Seite serverseitig)
  useEffect(() => {
    const loadInitial = async () => {
      try {
        setIsLoading(true);
        const response = await haukeApiService.getDirekterzeuger({ page: 1, limit: 50, sortBy: 'name', sortOrder: 'asc' });
        const data = response?.data || [];
        const normalized = data.map(p => ({
          id: p.hofId || p.id,
          name: p.name || p.producer_name || '',
          city: p.city || '',
          zip: p.postal_code || p.plz || ''
        })).filter(p => p.id && p.id !== currentFarmId);
        setAllProducers(normalized);
        // Nur setzen, wenn keine aktive Suche läuft (Race vermeiden)
        if (!searchQueryRef.current || searchQueryRef.current.trim() === '') {
          setSearchResults(normalized);
        }
      } catch (error) {
        console.error('Error loading initial producers:', error);
        setAllProducers([]);
        if (!searchQueryRef.current || searchQueryRef.current.trim() === '') {
          setSearchResults([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadInitial();
  }, [currentFarmId]);

  // Suche nach Direkterzeugern
  const handleSearch = async (query) => {
    setSearchQuery(query);
    searchQueryRef.current = query;
    
    if (!query.trim()) {
      setSearchResults(allProducers);
      return;
    }

    try {
      const requestId = ++lastRequestIdRef.current;
      setIsLoading(true);

      // Strukturierte serverseitige Suche
      const isZip = /^\d{3,5}$/.test(query.trim());
      const params = isZip
        ? { page: 1, limit: 50, plz: query.trim(), sortBy: 'postal_code', sortOrder: 'asc' }
        : { page: 1, limit: 50, search: query.trim(), sortBy: 'name', sortOrder: 'asc' };

      const response = await haukeApiService.getDirekterzeuger(params);
      const data = response?.data || [];
      const normalized = data.map(p => ({
        id: p.hofId || p.id,
        name: p.name || p.producer_name || '',
        city: p.city || '',
        zip: p.postal_code || p.plz || ''
      })).filter(p => p.id && p.id !== currentFarmId);
      if (requestId === lastRequestIdRef.current) {
        setSearchResults(normalized);
      }
    } catch (error) {
      console.error('Error searching producers:', error);
      // Fehler nur anwenden, wenn aktuell
      if (query === searchQueryRef.current) {
        setSearchResults([]);
      }
    } finally {
      // Lade-Status nur für aktuellste Anfrage zurücksetzen
      if (lastRequestIdRef.current === 0 || query === searchQueryRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleProducerSelect = (producer) => {
    onProducerSelect({
      producer_id: producer.id,
      producer_name: producer.name,
      producer_city: producer.city
    });
    setShowResults(false);
    setSearchQuery('');
  };

  const clearSelection = () => {
    onProducerSelect({
      producer_id: '',
      producer_name: '',
      producer_city: ''
    });
  };

  return (
    <div className={`producer-selector ${className}`}>
      <div className="producer-search" style={{ position: 'relative', zIndex: 1 }}>
        <div className="search-header">
          <h4>Anderen Produzenten auswählen</h4>
          <p>Suchen Sie nach PLZ, Ort oder Hofname</p>
        </div>
        
        <div className="search-input-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="PLZ, Ort oder Hofname eingeben..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch(searchQuery); setShowResults(true); } }}
            className="search-input"
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => { handleSearch(searchQuery); setShowResults(true); }}
            disabled={isLoading}
            title="Suche starten"
          >
            <i className="material-icons">search</i>
          </button>
        </div>

        {showResults && (
          <div
            className="search-results"
            style={resultsInFlow ? {
              position: 'static',
              zIndex: 'auto',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              marginTop: '6px'
            } : {
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 2147483647,
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              marginTop: '6px'
            }}
          >
            {isLoading ? (
              <div className="loading-state">
                <i className="material-icons">refresh</i>
                Suche läuft...
              </div>
            ) : searchResults.length > 0 ? (
              <div className="results-list">
                <div className="results-header">
                  <span>{searchResults.length} Höfe gefunden</span>
                  <button 
                    type="button" 
                    className="close-results"
                    onClick={() => setShowResults(false)}
                  >
                    <i className="material-icons">close</i>
                  </button>
                </div>
                <div className="results-items">
                  {searchResults.map(producer => (
                    <div 
                      key={producer.id}
                      className="result-item"
                      onClick={() => handleProducerSelect(producer)}
                    >
                      <div className="producer-info">
                        <h5>{producer.name}</h5>
                        <p>{producer.zip} {producer.city}</p>
                      </div>
                      <i className="material-icons">chevron_right</i>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-results">
                <i className="material-icons">search_off</i>
                <p>Keine Höfe gefunden</p>
                <small>Versuchen Sie eine andere PLZ oder einen anderen Ort</small>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProducerSelector;